import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { selectBuchungen } from 'modules/finanzen/features/finanzenSlice';
import {
  monatsReihenFuerJahr,
  vorhandeneJahre,
  summen,
  jahrVon,
} from 'modules/finanzen/features/berechnung';
import {
  selectInvestitionen,
  selectDepotWerte,
  selectDokumente,
  ladeDokumentHerunter,
} from 'modules/investitionen/features/investitionenSlice';
import { investitionenJeJahr } from 'modules/investitionen/features/berechnung';
import { formatKurs } from 'core/utils/format';
import BackToOverview from 'core/components/BackToOverview';
import GesichertGate from 'modules/finanzen/components/GesichertGate';
import GewinnBadge from 'modules/investitionen/components/GewinnBadge';
import ModulTabs from 'modules/finanzen/components/ModulTabs';

/**
 * Jahresansicht des Finanzen-Moduls: Monatsbalken Einnahmen vs. Ausgaben und die
 * Jahres-Kennzahlen. Nur im gesicherten Modus erreichbar.
 */
export default function JahresuebersichtPage() {
  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-12">
      <div className="mb-6">
        <BackToOverview />
      </div>
      <GesichertGate>
        <Jahr />
      </GesichertGate>
    </div>
  );
}

/** Entsperrte Ansicht: Jahresauswahl, Kennzahlen und Monats-Balkendiagramm. */
function Jahr() {
  const buchungen = useSelector(selectBuchungen);
  const jahre = useMemo(() => vorhandeneJahre(buchungen), [buchungen]);
  const aktuellesJahr = String(new Date().getFullYear());
  const [jahr, setJahr] = useState(jahre.includes(aktuellesJahr) ? aktuellesJahr : jahre[0] || aktuellesJahr);

  const reihen = useMemo(() => monatsReihenFuerJahr(buchungen, jahr), [buchungen, jahr]);
  const jahresSummen = useMemo(
    () => summen(buchungen.filter((b) => jahrVon(b.datum) === String(jahr))),
    [buchungen, jahr]
  );

  return (
    <>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-3xl font-semibold text-ink">📊 Jahresübersicht</h1>
        <select
          value={jahr}
          onChange={(e) => setJahr(e.target.value)}
          className="rounded-lg border border-line bg-paper px-3 py-2 text-sm text-ink focus:border-brand-600 focus:outline-none"
        >
          {(jahre.length ? jahre : [aktuellesJahr]).map((j) => (
            <option key={j} value={j}>
              {j}
            </option>
          ))}
        </select>
      </header>

      <ModulTabs />

      <div className="mb-6 grid grid-cols-3 gap-3">
        <Kennzahl label="Einnahmen" wert={formatKurs(jahresSummen.einnahmen)} ton="text-gain" />
        <Kennzahl label="Ausgaben" wert={formatKurs(jahresSummen.ausgaben)} ton="text-loss" />
        <Kennzahl
          label="Saldo"
          wert={formatKurs(jahresSummen.saldo)}
          ton={jahresSummen.saldo >= 0 ? 'text-gain' : 'text-loss'}
        />
      </div>

      <div className="mb-8 rounded-2xl border border-line bg-paper-soft p-5">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-muted">
          Einnahmen und Ausgaben je Monat
        </h2>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={reihen}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} width={70} tickFormatter={(w) => formatKurs(w)} />
            <Tooltip formatter={(wert) => formatKurs(wert)} />
            <Legend />
            <Bar dataKey="einnahmen" name="Einnahmen" fill="#10b981" radius={[4, 4, 0, 0]} />
            <Bar dataKey="ausgaben" name="Ausgaben" fill="#ef4444" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <InvestitionenJahre />
    </>
  );
}

/** Investitionen und Steuer-Dokumente je Anlage-Jahr (aus dem Positions-Datum). */
function InvestitionenJahre() {
  const dispatch = useDispatch();
  const orte = useSelector(selectInvestitionen);
  const depotWerte = useSelector(selectDepotWerte);
  const dokumente = useSelector(selectDokumente);

  const ortName = useMemo(() => {
    const map = {};
    orte.forEach((o) => {
      map[o.uuid] = o.institut;
    });
    return map;
  }, [orte]);

  const jahre = useMemo(() => {
    const kennzahlen = investitionenJeJahr(orte, depotWerte);
    const dokMap = {};
    dokumente.forEach((d) => {
      const jahr = d.jahr || 'Ohne Jahr';
      (dokMap[jahr] = dokMap[jahr] || []).push(d);
    });
    const alle = new Set([...kennzahlen.map((k) => k.jahr), ...Object.keys(dokMap)]);
    return [...alle]
      .sort((a, b) => String(b).localeCompare(String(a)))
      .map((jahr) => ({ jahr, kennzahl: kennzahlen.find((k) => k.jahr === jahr), dokumente: dokMap[jahr] || [] }));
  }, [orte, depotWerte, dokumente]);

  if (jahre.length === 0) return null;

  return (
    <section>
      <h2 className="mb-3 font-serif text-xl font-semibold text-ink">Investitionen & Steuer-Dokumente je Jahr</h2>
      <div className="space-y-6">
        {jahre.map((j) => (
          <div key={j.jahr} className="rounded-2xl border border-line bg-paper-soft p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-serif text-2xl font-semibold text-ink">{j.jahr}</h3>
              {j.kennzahl && (
                <div className="flex flex-wrap items-center gap-4 text-sm text-ink-muted">
                  <span>Investiert {formatKurs(j.kennzahl.investiert)}</span>
                  <span>Wert {formatKurs(j.kennzahl.wert)}</span>
                  <GewinnBadge gv={j.kennzahl.gv} gvProz={j.kennzahl.gvProz} />
                </div>
              )}
            </div>
            {j.dokumente.length === 0 ? (
              <p className="text-sm text-ink-muted">Keine Dokumente für dieses Jahr.</p>
            ) : (
              <ul className="divide-y divide-line rounded-xl border border-line bg-paper">
                {j.dokumente.map((d) => (
                  <li key={d.uuid} className="flex flex-wrap items-center gap-3 px-4 py-3">
                    <span className="text-xl" aria-hidden="true">📄</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-ink">{d.name}</p>
                      <p className="text-xs text-ink-muted">{ortName[d.investitionUuid] || 'Unbekannter Ort'}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => dispatch(ladeDokumentHerunter(d))}
                      className="rounded-lg px-3 py-1.5 text-sm text-brand-600 hover:bg-brand-600/10"
                    >
                      Herunterladen
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/** Eine einzelne Kennzahl-Kachel. */
function Kennzahl({ label, wert, ton }) {
  return (
    <div className="rounded-2xl border border-line bg-paper-soft p-4 text-center">
      <p className="text-xs uppercase tracking-wide text-ink-muted">{label}</p>
      <p className={`mt-1 font-serif text-xl font-semibold tabular-nums ${ton}`}>{wert}</p>
    </div>
  );
}
